import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import {
  CheckCircle2,
  Circle,
  Clock,
  FileText,
  PenTool,
  AlertCircle,
  ChevronDown,
  ChevronRight,
  Plus,
} from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { format } from "date-fns";
import { cn } from "@/lib/utils";

interface TaskPhaseProgressProps {
  taskId: string;
  unaId: string;
  onDocumentRequired?: (documentType: string) => void;
  onSignatureRequired?: (documentId: string) => void;
}

interface PhaseData {
  id: string;
  task_id: string;
  una_id: string;
  phase_number: number;
  phase_name: string;
  phase_description: string;
  order_number: number;
  status: string;
  requirements: any[];
  document_requirements: any[];
  signature_requirements: any[];
  completed_at: string | null;
  estimated_duration_days: number;
  blocked_by_phase_id: string | null;
  created_at: string;
}

export function TaskPhaseProgress({ taskId, unaId, onDocumentRequired, onSignatureRequired }: TaskPhaseProgressProps) {
  const { toast } = useToast();
  const [phases, setPhases] = useState<PhaseData[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPhaseIndex, setCurrentPhaseIndex] = useState(0);
  const [expandedPhases, setExpandedPhases] = useState<Set<string>>(new Set());

  useEffect(() => {
    loadPhases();
  }, [taskId]);

  const loadPhases = async () => {
    try {
      const { data, error } = await supabase
        .from('task_phases')
        .select('*')
        .eq('task_id', taskId)
        .order('order_number', { ascending: true });

      if (error) throw error;

      if (data && data.length > 0) {
        setPhases(data as any);
        // Find first incomplete phase
        const firstIncomplete = data.findIndex(p => p.status !== 'completed');
        setCurrentPhaseIndex(firstIncomplete >= 0 ? firstIncomplete : data.length - 1);
        // Auto-expand current phase
        if (data[firstIncomplete >= 0 ? firstIncomplete : 0]) {
          setExpandedPhases(new Set([data[firstIncomplete >= 0 ? firstIncomplete : 0].id]));
        }
      }
    } catch (error) {
      console.error('Error loading phases:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load task phases",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRequirementToggle = async (phaseId: string, requirementId: string, completed: boolean) => {
    try {
      const phase = phases.find(p => p.id === phaseId);
      if (!phase) return;

      const updatedRequirements = phase.requirements.map((req: any) =>
        req.id === requirementId ? { ...req, completed } : req
      );

      const { error } = await supabase
        .from('task_phases')
        .update({ requirements: updatedRequirements })
        .eq('id', phaseId);

      if (error) throw error;

      await loadPhases();
      toast({
        title: completed ? "Requirement Completed" : "Requirement Unchecked",
        description: "Phase progress updated",
      });
    } catch (error) {
      console.error('Error updating requirement:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to update requirement",
      });
    }
  };

  const canAdvancePhase = (phase: PhaseData): boolean => {
    // Check all manual requirements
    const allManualComplete = phase.requirements.every((req: any) => req.completed);
    
    // Check all document requirements
    const allDocsComplete = phase.document_requirements.every(
      (doc: any) => !doc.required || ['finalized', 'signed'].includes(doc.status)
    );
    
    // Check all signature requirements
    const allSigsComplete = phase.signature_requirements.every(
      (sig: any) => !sig.required || sig.status === 'completed'
    );

    return allManualComplete && allDocsComplete && allSigsComplete;
  };

  const handleCompletePhase = async (phaseId: string) => {
    try {
      const { error } = await supabase
        .from('task_phases')
        .update({
          status: 'completed',
          completed_at: new Date().toISOString()
        })
        .eq('id', phaseId);

      if (error) throw error;

      toast({
        title: "Phase Completed! 🎉",
        description: "Moving to next phase...",
      });

      await loadPhases();
    } catch (error) {
      console.error('Error completing phase:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to complete phase",
      });
    }
  };

  const togglePhaseExpanded = (phaseId: string) => {
    setExpandedPhases(prev => {
      const newSet = new Set(prev);
      if (newSet.has(phaseId)) {
        newSet.delete(phaseId);
      } else {
        newSet.add(phaseId);
      }
      return newSet;
    });
  };

  const calculateProgress = (phase: PhaseData): number => {
    let totalItems = phase.requirements.length + phase.document_requirements.length + phase.signature_requirements.length;
    if (totalItems === 0) return 0;

    let completedItems = 0;
    completedItems += phase.requirements.filter((r: any) => r.completed).length;
    completedItems += phase.document_requirements.filter((d: any) => ['finalized', 'signed'].includes(d.status)).length;
    completedItems += phase.signature_requirements.filter((s: any) => s.status === 'completed').length;

    return Math.round((completedItems / totalItems) * 100);
  };

  const calculateOverallProgress = (): number => {
    if (phases.length === 0) return 0;
    const completedPhases = phases.filter(p => p.status === 'completed').length;
    return Math.round((completedPhases / phases.length) * 100);
  };

  if (loading) {
    return <div className="text-center py-8">Loading phases...</div>;
  }

  if (phases.length === 0) {
    return (
      <Card className="p-8 text-center">
        <Circle className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
        <h3 className="font-semibold mb-2">No Phases Configured</h3>
        <p className="text-sm text-muted-foreground">
          This task doesn't have phases. You can add them to track multi-step workflows.
        </p>
      </Card>
    );
  }

  const overallProgress = calculateOverallProgress();

  return (
    <div className="space-y-6">
      {/* Overall Progress */}
      <div className="space-y-2">
        <div className="flex items-center justify-between text-sm">
          <span className="font-medium">Overall Progress</span>
          <span className="text-muted-foreground">{overallProgress}% Complete</span>
        </div>
        <Progress value={overallProgress} className="h-2" />
        <p className="text-xs text-muted-foreground">
          {phases.filter(p => p.status === 'completed').length} of {phases.length} phases completed
        </p>
      </div>

      {/* Phase Cards */}
      <div className="space-y-4">
        {phases.map((phase, index) => {
          const progress = calculateProgress(phase);
          const isExpanded = expandedPhases.has(phase.id);
          const isCurrent = index === currentPhaseIndex;
          const isCompleted = phase.status === 'completed';
          const canComplete = canAdvancePhase(phase);

          return (
            <Collapsible
              key={phase.id}
              open={isExpanded}
              onOpenChange={() => togglePhaseExpanded(phase.id)}
            >
              <Card className={`${isCurrent && !isCompleted ? 'border-primary' : ''}`}>
                <CollapsibleTrigger asChild>
                  <button className="w-full p-4 text-left">
                    <div className="flex items-start gap-4">
                      <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 ${
                        isCompleted ? 'bg-green-500 border-green-500 text-white' : isCurrent ? 'bg-primary border-primary text-primary-foreground' : 'bg-background border-border'
                      }`}>
                        {isCompleted ? <CheckCircle2 className="h-5 w-5" /> : <span className="text-sm font-semibold">{phase.phase_number}</span>}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-start justify-between">
                          <div>
                            <h3 className="font-semibold">{phase.phase_name}</h3>
                            <p className="text-sm text-muted-foreground mt-1">{phase.phase_description}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            {isCompleted && <Badge className="bg-green-600">Completed</Badge>}
                            {isCurrent && !isCompleted && <Badge>Active</Badge>}
                            {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                          </div>
                        </div>
                        {!isCompleted && (
                          <div className="mt-3">
                            <Progress value={progress} className="h-1.5" />
                            <p className="text-xs text-muted-foreground mt-1">{progress}% complete</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </button>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="px-4 pb-4 ml-12 space-y-4">
                    {phase.requirements.map((req: any) => (
                      <div key={req.id} className="flex items-start gap-2">
                        <Checkbox checked={req.completed} onCheckedChange={(checked) => handleRequirementToggle(phase.id, req.id, checked as boolean)} disabled={isCompleted} />
                        <label className={`text-sm ${req.completed ? 'line-through text-muted-foreground' : ''}`}>{req.description}</label>
                      </div>
                    ))}
                    {!isCompleted && isCurrent && (
                      <Button onClick={() => handleCompletePhase(phase.id)} disabled={!canComplete} className="w-full">
                        {canComplete ? 'Complete Phase' : 'Complete All Requirements First'}
                      </Button>
                    )}
                  </div>
                </CollapsibleContent>
              </Card>
            </Collapsible>
          );
        })}
      </div>
    </div>
  );
}
